import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Car, Link2, Link2Off, PlusCircle } from "lucide-react";
import { useState } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useToast } from "@/hooks/use-toast";
import type { AssetPolicyLink } from "@shared/schema";

interface PolicyLinksResponse {
  links: AssetPolicyLink[];
}

interface InsuresPickerProps {
  clientId: string;
  policySectionId: string;
  policyRowId: string;
  policyType?: string;
  readOnly?: boolean;
}

const POLICY_LINKS_KEY = (clientId: string, policyRowId: string) =>
  ['/api/clients', clientId, 'asset-policy-links-policy', policyRowId] as const;

const SUGGESTIONS_KEY = (clientId: string) =>
  ['/api/clients', clientId, 'asset-policy-links', 'suggestions'] as const;

export function InsuresPicker({
  clientId,
  policySectionId,
  policyRowId,
  readOnly = false,
}: InsuresPickerProps) {
  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: linksData } = useQuery<PolicyLinksResponse>({
    queryKey: POLICY_LINKS_KEY(clientId, policyRowId),
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientId}/asset-policy-links?policyRowId=${policyRowId}`);
      if (!res.ok) throw new Error("Failed to load policy links");
      return res.json();
    },
    staleTime: Infinity,
  });

  const { data: suggestions } = useQuery({
    queryKey: SUGGESTIONS_KEY(clientId),
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientId}/asset-policy-links/suggestions`);
      if (!res.ok) throw new Error("Failed to load suggestions");
      return res.json() as Promise<Array<{
        vehicleSectionId: string;
        vehicleRowId: string;
        vehicleMakeModel: string;
        vehicleType: string;
        policyRowId: string;
        alreadyLinked: boolean;
      }>>;
    },
    staleTime: Infinity,
  });

  const unlinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const res = await apiRequest("DELETE", `/api/clients/${clientId}/asset-policy-links/${linkId}`);
      if (!res.ok) throw new Error("Failed to unlink");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: POLICY_LINKS_KEY(clientId, policyRowId) });
      queryClient.invalidateQueries({ queryKey: SUGGESTIONS_KEY(clientId) });
      toast({ title: "Vehicle link removed" });
    },
    onError: (e: Error) => toast({ title: "Unlink failed", description: e.message, variant: "destructive" }),
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire creates an asset-policy link row.
  const linkFlight = useSingleFlight();

  const linkMutation = useMutation({
    mutationFn: async ({ vehicleRowId, vehicleSectionId }: { vehicleRowId: string; vehicleSectionId: string }) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/asset-policy-links`, {
        vehicleSectionId,
        vehicleRowId,
        policySectionId,
        policyRowId,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to link");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: POLICY_LINKS_KEY(clientId, policyRowId) });
      queryClient.invalidateQueries({ queryKey: SUGGESTIONS_KEY(clientId) });
      queryClient.invalidateQueries({ queryKey: ['/api/clients', clientId, 'asset-policy-links'] });
      toast({ title: "Vehicle linked to policy" });
      setPickerOpen(false);
    },
    onError: (e: Error) => toast({ title: "Link failed", description: e.message, variant: "destructive" }),
  });

  const links = linksData?.links ?? [];

  // Build vehicle label lookup from suggestions data
  const vehicleLabels = new Map<string, { makeModel: string; vehicleType: string }>();
  if (suggestions) {
    for (const s of suggestions) {
      if (!vehicleLabels.has(s.vehicleRowId)) {
        vehicleLabels.set(s.vehicleRowId, { makeModel: s.vehicleMakeModel, vehicleType: s.vehicleType });
      }
    }
  }

  const linkedVehicles = links.map(l => ({
    link: l,
    label: vehicleLabels.get(l.vehicleRowId),
  }));

  const linkedVehicleRowIds = new Set(links.map(l => l.vehicleRowId));
  const unlinkableVehicles = suggestions
    ? suggestions
        .filter(s => !linkedVehicleRowIds.has(s.vehicleRowId))
        .reduce<Array<typeof suggestions[0]>>((acc, s) => {
          if (!acc.some(a => a.vehicleRowId === s.vehicleRowId)) acc.push(s);
          return acc;
        }, [])
    : [];

  return (
    <>
      {/* Panel header — matches asset-side sub-card structure */}
      <div className="flex items-center gap-2 mb-2">
        <Car className="h-3.5 w-3.5 text-muted-foreground" style={{ flexShrink: 0 }} />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Insures</span>
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs ml-auto gap-1 text-muted-foreground hover:text-primary"
            onClick={() => setPickerOpen(true)}
          >
            <PlusCircle className="h-3 w-3" />
            Link to vehicle
          </Button>
        )}
      </div>

      {/* Linked vehicles — card list matching asset-side panel cards */}
      {linkedVehicles.length === 0 && (
        <div
          className="rounded border px-3 py-2.5"
          style={{ borderColor: '#D4CDB8', backgroundColor: '#FDFCFA' }}
        >
          <p className="text-xs text-muted-foreground">
            No vehicles linked to this policy.
            {!readOnly && ' Use "Link to vehicle" to connect it.'}
          </p>
        </div>
      )}

      {linkedVehicles.map(({ link, label }) => {
        const displayName = label?.makeModel || label?.vehicleType || link.vehicleRowId.slice(-6);
        const subLabel = label?.makeModel ? label.vehicleType : undefined;

        return (
          <div
            key={link.id}
            className="rounded border mb-1.5 flex items-center gap-2 px-3 py-2"
            style={{ backgroundColor: '#FDFCFA', borderColor: '#D4CDB8' }}
          >
            <Car className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-foreground truncate block">{displayName}</span>
              {subLabel && (
                <span className="text-xs text-muted-foreground">{subLabel}</span>
              )}
            </div>
            {!readOnly && (
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
                title="Remove vehicle link"
                onClick={() => unlinkMutation.mutate(link.id)}
                disabled={unlinkMutation.isPending}
              >
                <Link2Off className="h-3 w-3" />
              </Button>
            )}
          </div>
        );
      })}

      {/* Vehicle picker dialog */}
      {pickerOpen && (
        <Dialog open onOpenChange={open => { if (!open) setPickerOpen(false); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Link to a Vehicle</DialogTitle>
              <DialogDescription>
                Select a vehicle this policy insures.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {unlinkableVehicles.length === 0 && (
                <p className="text-sm text-muted-foreground">No vehicles available to link.</p>
              )}
              {unlinkableVehicles.map(vehicle => (
                <div
                  key={vehicle.vehicleRowId}
                  className="flex items-center justify-between p-2.5 rounded border gap-2"
                  style={{ borderColor: '#D4CDB8', backgroundColor: '#FDFCFA' }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{vehicle.vehicleMakeModel || vehicle.vehicleType}</p>
                      {vehicle.vehicleMakeModel && (
                        <p className="text-xs text-muted-foreground">{vehicle.vehicleType}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 h-7 text-xs"
                    onClick={() => linkFlight.runQuiet(() => linkMutation.mutateAsync({
                      vehicleRowId: vehicle.vehicleRowId,
                      vehicleSectionId: vehicle.vehicleSectionId,
                    }))}
                    disabled={linkFlight.inFlight || linkMutation.isPending}
                  >
                    <Link2 className="h-3 w-3 mr-1" />
                    Link
                  </Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPickerOpen(false)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
